import {
  AUTH_CAPS_GLOBAL,
  DEFAULT_AUTH_CAPABILITIES,
  type AuthCapabilities,
} from '../constants';

/**
 * Read the deployment's auth capability map injected by the server's
 * /login route as `window.__FRED_AUTH_CAPS__`. Pure read of a single
 * synchronous global; no React state, no effect, no first-paint flash.
 *
 * Falls back to {@link DEFAULT_AUTH_CAPABILITIES} (all-off) when:
 *   - the global is missing (dev server, hot-reload, deep-linked SPA),
 *   - the global is present but malformed,
 *   - any individual flag is missing or non-boolean.
 *
 * Each call re-reads the global by design — strict-mode double-renders
 * stay deterministic and the value is trivially small to read twice.
 */
export function readAuthCaps(): AuthCapabilities {
  if (typeof window === 'undefined') {
    return DEFAULT_AUTH_CAPABILITIES;
  }
  const raw = (window as unknown as Record<string, unknown>)[AUTH_CAPS_GLOBAL];
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_AUTH_CAPABILITIES;
  }
  const partial = raw as Partial<Record<keyof AuthCapabilities, unknown>>;
  return {
    password: typeof partial.password === 'boolean' ? partial.password : DEFAULT_AUTH_CAPABILITIES.password,
    magic_link:
      typeof partial.magic_link === 'boolean' ? partial.magic_link : DEFAULT_AUTH_CAPABILITIES.magic_link,
    oidc: typeof partial.oidc === 'boolean' ? partial.oidc : DEFAULT_AUTH_CAPABILITIES.oidc,
  };
}

export function useAuthCaps(): AuthCapabilities {
  // Reading at render time (synchronous, no useEffect) is intentional:
  // the global is set before main.tsx evaluates, so the very first
  // render already sees the correct map.
  return readAuthCaps();
}
